import { officeExtentions } from "../../../src/taskpane/config/constants";
import { dataSourceItem, FilterItem, FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { ClientTypeContext, INERT_CLIENT_TYPES } from "../../../src/taskpane/components/helpers/dataSourceFieldHelper";
import { DatasourceHelper } from "../../../src/taskpane/components/helpers/dataSourceHelper";
import { ParsedPreselectFilters } from "./preselectFilterParser";
import { buildOfficeBucketLookup, DOCICON_FIELD_NAME, findSchemaField, isDateTimeField, isExcludedFilterField, normalizeExtension } from "./preselectFieldMatching";

export interface SynthesizeContext {
  officeExtentions: typeof officeExtentions;
  datasourceItems: dataSourceItem[];
  siteDisplayName: string;
  documentLibrary: string;
  // Mirrors the tenant setting the section build honours; synthesized sections
  // must apply the same field exclusions or a deep link resurrects hidden fields.
  hideTitleFieldFilter?: boolean;
  // Optional so existing raw-literal fixtures keep compiling; buildSynthesizeContext
  // always emits it. The fast path and the reconcile path MUST carry the same value or
  // their datasourceMutated verdicts drift and preselect sections get dropped.
  clientTypes?: ClientTypeContext;
}

// The ONE way to assemble a SynthesizeContext. The fast path and the reconcile's
// build-loss fallback must synthesize from identical inputs — the fast-vs-reconciled
// signature comparison assumes it — so both call this instead of keeping inline
// object literals that could drift.
export const buildSynthesizeContext = (
  datasourceItems: dataSourceItem[] | undefined,
  currentDirectory: any,
  hideTitleFieldFilter?: boolean,
  clientTypes: ClientTypeContext = INERT_CLIENT_TYPES,
): SynthesizeContext => ({
  officeExtentions,
  datasourceItems: datasourceItems ?? [],
  siteDisplayName: currentDirectory?.siteDisplayName ?? "",
  documentLibrary: currentDirectory?.DocumentLibrary ?? "",
  hideTitleFieldFilter: !!hideTitleFieldFilter,
  clientTypes,
});

export interface SynthesizeResult {
  sections: FilterSection[];
  appliedCount: number;
}

const isDatasourceMutatedTextField = (field: any, ctx: SynthesizeContext): boolean => {
  if (field?.FieldType !== "Text") {
    return false;
  }
  const datasourceField = DatasourceHelper.getInputDataSource(
    ctx.datasourceItems,
    field.DisplayName,
    [ctx.siteDisplayName ?? ""],
    [ctx.documentLibrary ?? ""],
    ctx.clientTypes ?? INERT_CLIENT_TYPES,
  );
  if (!Array.isArray(datasourceField?.boundFields) || datasourceField.boundFields.length === 0) {
    return false;
  }
  const bounded = datasourceField.boundFields.find(item =>
    item.name?.split(",").some(name => name.trim().toLowerCase() === field.DisplayName?.trim().toLowerCase()),
  );
  return !!bounded?.multiSelect;
};

/**
 * Builds minimal filter sections directly from the list field schema and the
 * requested pre-select values — WITHOUT calling RenderListFilterData and without
 * needing serverRelPath. The resulting sections drive the first (filtered) grid
 * fetch; the reactive reconcile later rebuilds the real sections and confirms or
 * corrects them. Item shapes mirror FileFilterHelper so the query builder treats
 * them identically to a manual selection.
 */
interface SynthesizedSectionEntry {
  items: FilterItem[];
  seenValues: Set<string>;
  datasourceMutated: boolean;
}

export const synthesizePreselectSections = (
  schemaFields: any[],
  parsed: ParsedPreselectFilters,
  ctx: SynthesizeContext,
): SynthesizeResult => {
  const officeBucketLookup = buildOfficeBucketLookup(ctx.officeExtentions);
  // One entry per RESOLVED schema field: two querystring keys that resolve to
  // the same field (?filetype=…&DocIcon=…) must merge into one section — the
  // resolver matches both keys into one built section and dedupes through a
  // Set, so two fast sections for one field would double that field's values
  // in the multiset signature and force a spurious corrective re-fetch.
  const entries = new Map<any, SynthesizedSectionEntry>();
  let appliedCount = 0;

  // Duplicate values collapse: the resolver dedupes matches through a Set and
  // SharePoint FilterValue comparison is case-insensitive, so a repeated or
  // case-differing duplicate (?rubriek=balans;balans) is the SAME query —
  // pushing it twice would put a duplicate into the multiset signature and
  // guarantee a redundant corrective re-fetch. Office buckets dedupe here too:
  // every extension of one family shares the bucket's pre-encoded value.
  const pushUnique = (entry: SynthesizedSectionEntry, title: string, value: string, fileType?: string): void => {
    if (entry.seenValues.has(value.toLowerCase())) {
      return;
    }
    entry.seenValues.add(value.toLowerCase());
    // preselectInjected: synthesized items are schema-only guesses, not values
    // confirmed against the library — stamped exactly like the build-loss
    // fallback's items so a Reset that fires while they are live (a user clear
    // cancelling the reconcile mid-flight) strips them instead of stranding a
    // fake truncated value list the panel's build gate would then protect.
    const item: FilterItem = { title, value, selected: true, preselectInjected: true };
    if (fileType) {
      item.fileType = fileType;
    }
    entry.items.push(item);
    appliedCount += 1;
  };

  for (const field of parsed.fields) {
    const schemaField = findSchemaField(schemaFields, field.key);
    if (!schemaField || isDateTimeField(schemaField) || isExcludedFilterField(schemaField, ctx.hideTitleFieldFilter)) {
      continue;
    }

    const isDocIcon = schemaField.RealFieldName === DOCICON_FIELD_NAME;
    let entry = entries.get(schemaField);
    if (!entry) {
      entry = {
        items: [],
        seenValues: new Set<string>(),
        datasourceMutated: !isDocIcon && isDatasourceMutatedTextField(schemaField, ctx),
      };
      entries.set(schemaField, entry);
    }

    for (const rawValue of field.values) {
      if (isDocIcon) {
        const extension = normalizeExtension(rawValue);
        // "?filetype=." normalizes to "" — skip it or an empty, selected,
        // blank-labelled row reaches the panel and an empty FilterValue the wire.
        if (!extension) {
          continue;
        }
        const bucket = officeBucketLookup[extension];
        if (bucket) {
          pushUnique(entry, bucket.title, bucket.values, bucket.fileType);
        } else {
          // No fileType: that flag drives the encoding exemption reserved for
          // office buckets (pre-encoded extension lists) in the code paths that
          // encode (the taskpane grid builder and the strike probe), and the
          // resolver's raw injections carry no fileType either — parity holds
          // by construction. NOTE: the content manager's own grid slice
          // (api-sharepoint-slice renderListAsStream) emits filter values raw
          // for ALL items — a pre-existing gap documented in
          // filterValueEncoding.ts, not something this flag can influence.
          pushUnique(entry, extension, extension);
        }
      } else if (entry.datasourceMutated) {
        rawValue
          .split(",")
          .map(part => part.trim())
          .filter(Boolean)
          .forEach(part => pushUnique(entry, part, part));
      } else {
        pushUnique(entry, rawValue, rawValue);
      }
    }
  }

  const sections: FilterSection[] = [];
  entries.forEach((entry, schemaField) => {
    if (entry.items.length === 0) {
      return;
    }
    const section: FilterSection = {
      filterField: schemaField,
      items: entry.items,
      showMoreExpanded: false,
      hasSlider: false,
      showSlider: false,
    };
    if (entry.datasourceMutated) {
      section.datasourceMutated = true;
    }
    sections.push(section);
  });

  return { sections, appliedCount };
};
